'use client';

import { useEffect, useRef } from 'react';
import Image, { StaticImageData } from 'next/image';

interface ParallaxBandProps {
  image: StaticImageData;
  alt: string;
  /**
   * Parallax intensity: subtle drift only — lower bands ~0.07. Pass 0 for a
   * static band: the image then fills the band exactly (no overscan), which
   * keeps a band-cropped imageOverlay pixel-aligned with the photo.
   */
  speed?: number;
  priority?: boolean;
  id?: string;
  className?: string;
  /** Tailwind classes for the overlay scrim. */
  scrimClassName?: string;
  /** Tailwind object-position class for the image, e.g. 'object-bottom'. */
  imageClassName?: string;
  /** Overlay rendered above the scrim, cropped to the band (not the moving wrapper). */
  imageOverlay?: React.ReactNode;
  children?: React.ReactNode;
}

export default function ParallaxBand({
  image,
  alt,
  speed = 0.2,
  priority = false,
  id,
  className = '',
  scrimClassName = 'bg-gradient-to-b from-black/30 to-black/60',
  imageClassName = '',
  imageOverlay,
  children,
}: ParallaxBandProps) {
  const bandRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const band = bandRef.current;
    const imgWrap = imgWrapRef.current;
    if (!band || !imgWrap) return;
    if (speed === 0) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let ticking = false;
    let rafId = 0;
    const update = () => {
      ticking = false;
      // Parallax can stutter on mobile; render static below md.
      if (window.innerWidth < 768) {
        imgWrap.style.transform = '';
        return;
      }
      const r = band.getBoundingClientRect();
      const vh = window.innerHeight;
      if (r.bottom < 0 || r.top > vh) return;
      const progress = r.top + r.height / 2 - vh / 2;
      // Cap the speed so the full travel fits the wrapper's 20% overscan —
      // motion stays continuous (no clamp "stick") and edges are never exposed.
      const maxOffset = r.height * 0.2;
      const maxProgress = (vh + r.height) / 2;
      const effectiveSpeed = Math.min(speed, maxOffset / maxProgress);
      imgWrap.style.transform = `translate3d(0, ${progress * effectiveSpeed}px, 0)`;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        rafId = requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [speed]);

  return (
    <section ref={bandRef} id={id} className={`relative overflow-hidden bg-[#0d1b18] ${className}`}>
      {/* Oversized so the parallax offset never exposes band edges; exact fit when static. */}
      <div
        ref={imgWrapRef}
        className={
          speed === 0
            ? 'absolute -inset-px'
            : 'absolute -inset-x-px -top-[20%] h-[140%] will-change-transform'
        }
      >
        <Image
          src={image}
          alt={alt}
          fill
          priority={priority}
          sizes="100vw"
          className={`object-cover saturate-[0.92] contrast-[1.06] ${imageClassName}`}
        />
      </div>
      <div aria-hidden="true" className={`absolute inset-0 ${scrimClassName}`} />
      {/* Phone crops land text on unpredictable parts of the photo; add a
          uniform darkening layer below md for legibility insurance. */}
      <div aria-hidden="true" className="absolute inset-0 bg-[#081210]/25 md:hidden" />
      {imageOverlay}
      <div className="relative z-10 w-full">{children}</div>
    </section>
  );
}
